import { fetchGraphql, isFormattedRejectionError } from '@zoopla/fetch-graphql';
import { setupLogger } from '@zoopla/logger';
import {
    GetTrackedPropertyDocument,
    GetTrackedPropertyQuery,
    GetTrackedPropertyQueryVariables,
} from '../queries';
import { LOGGER_METADATA } from '../helpers';
import { ConfidenceLevel } from '../graphql.generated';

type GetIsTrackedPropertyOptions = {
    signal: AbortSignal;
};

const formatErrorMessage = (e: unknown) => {
    if (e instanceof Error) return e.message;
    // fetchGraphql returns a formatted error message, so we check if it is
    // one from fetchGraphql, and if it is return that value.
    if (isFormattedRejectionError(e)) return e.error.message;
    return 'Unknown error';
};

export type SaleEstimate = {
    lowerPrice: number;
    currentPrice: number;
    upperPrice: number;
    confidenceLevel: ConfidenceLevel;
};

export type RentEstimate = {
    currentPrice: number;
};

export type TrackedProperty = {
    uprn: string;
    outcode: string;
    postcode: string;
    liveListingIds: string[];
    bedrooms?: number;
    bathrooms?: number;
    isTracked: boolean;
    saleEstimate?: SaleEstimate;
    rentEstimate?: RentEstimate;
};

export const getValidTrackedProperty = (
    uprn: string,
    property: GetTrackedPropertyQuery['property']
): TrackedProperty | null => {
    if (!property?.address) return null;

    const {
        address,
        attributes,
        trackedProperty,
        liveListings,
        saleEstimate,
        rentEstimate,
    } = property;

    const outcode = address.outcode?.trim();
    const postcode = address.postcode.trim();

    if (!outcode || !postcode) return null;

    const { bedrooms, bathrooms } = attributes || {};

    return {
        uprn,
        outcode,
        postcode,
        isTracked: !!trackedProperty?.isTracked,
        liveListingIds: liveListings?.map((listing) => listing.listingId) || [],
        // Optional values are removed if falsey
        ...(bedrooms ? { bedrooms } : {}),
        ...(bathrooms ? { bathrooms } : {}),
        ...(saleEstimate ? { saleEstimate } : {}),
        ...(rentEstimate ? { rentEstimate } : {}),
    };
};

export const getTrackedProperty = async (
    uprn: string,
    options: GetIsTrackedPropertyOptions
): Promise<TrackedProperty | null> => {
    const logger = setupLogger(LOGGER_METADATA);

    // Early exit to avoid unnecessary call to GraphQL if UPRN is an empty
    // string
    if (!uprn) return null;

    try {
        const variables: GetTrackedPropertyQueryVariables = {
            uprn,
        };

        const { data, errors } = await fetchGraphql<
            GetTrackedPropertyQuery,
            GetTrackedPropertyQueryVariables
        >(GetTrackedPropertyDocument, variables, {
            signal: options.signal,
        });

        if (errors) {
            throw new Error(errors.map((e) => e.message).join(', '));
        }

        return getValidTrackedProperty(uprn, data?.property);
    } catch (err) {
        if (!options.signal.aborted) {
            logger.warn(
                { err: formatErrorMessage(err) },
                `Failed to get tracked property state for UPRN: ${uprn}`
            );
        }
    }

    return null;
};
